import { useChangerQuantite, type Order } from '@/lib/orders';
import { formatMoney } from '@/lib/utils';

/**
 * Panier persistant — colonne de droite de l'écran de commande (plan §5.4).
 * Les totaux affichés viennent TOUJOURS du serveur : rien n'est recalculé ici,
 * pour qu'aucun écart ne puisse apparaître entre l'écran et le bon de commande.
 */
export default function Panier({
  order,
  onSoumettre,
}: {
  order: Order;
  onSoumettre: () => void;
}) {
  const changer = useChangerQuantite(order.id);
  const vide = order.lines.length === 0;

  return (
    <aside className="bg-card flex h-full flex-col border-l">
      <header className="border-b p-4">
        <p className="text-muted-foreground text-sm">Client</p>
        <p className="text-lg font-semibold">{order.customer.name}</p>
        <p className="text-muted-foreground font-mono text-xs">{order.customer.custNum}</p>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        {vide ? (
          <p className="text-muted-foreground p-6 text-center text-sm">
            Panier vide. Touchez un produit à gauche pour l'ajouter.
          </p>
        ) : (
          <ul className="space-y-2">
            {order.lines.map((l) => (
              <li key={l.id} className="rounded-lg border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-semibold">{l.partNumber}</p>
                    <p className="text-sm leading-snug">{l.description}</p>
                  </div>
                  <p className="shrink-0 font-semibold tabular-nums">{formatMoney(l.lineTotal)}</p>
                </div>

                {l.promoLabel && (
                  <p className="mt-1 text-xs font-medium text-green-700 dark:text-green-400">
                    {l.promoLabel} · −{formatMoney(l.discountAmount)}
                  </p>
                )}

                <div className="mt-2 flex items-center gap-2">
                  {/* Cibles de 48 px : on ajuste les quantités debout, au doigt. */}
                  <button
                    aria-label={`Retirer une unité de ${l.partNumber}`}
                    onClick={() => changer.mutate({ lineId: l.id, qty: l.qty - 1 })}
                    disabled={changer.isPending}
                    className="border-input h-12 w-12 rounded-lg border text-xl font-bold disabled:opacity-40"
                  >
                    −
                  </button>
                  <span className="w-12 text-center text-lg font-semibold tabular-nums">{l.qty}</span>
                  <button
                    aria-label={`Ajouter une unité de ${l.partNumber}`}
                    onClick={() => changer.mutate({ lineId: l.id, qty: l.qty + 1 })}
                    disabled={changer.isPending}
                    className="border-input h-12 w-12 rounded-lg border text-xl font-bold disabled:opacity-40"
                  >
                    +
                  </button>
                  <span className="text-muted-foreground ml-auto text-xs">
                    {formatMoney(l.unitPrice)} / {l.uom}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="space-y-3 border-t p-4">
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Sous-total</dt>
            <dd className="tabular-nums">{formatMoney(order.subtotal)}</dd>
          </div>
          {order.discountTotal !== '0.00' && (
            <div className="flex justify-between text-green-700 dark:text-green-400">
              <dt>Rabais</dt>
              <dd className="tabular-nums">−{formatMoney(order.discountTotal)}</dd>
            </div>
          )}
        </dl>

        {/* Total en très gros : c'est le chiffre que le rep montre au client. */}
        <div className="flex items-baseline justify-between border-t pt-3">
          <span className="font-medium">Total</span>
          <span className="text-3xl font-bold tabular-nums">{formatMoney(order.total)}</span>
        </div>
        <p className="text-muted-foreground text-xs">Montants avant taxes.</p>

        <button
          onClick={onSoumettre}
          disabled={vide}
          className="bg-primary text-primary-foreground h-16 w-full rounded-lg text-lg font-semibold disabled:opacity-40"
        >
          Réviser et soumettre
        </button>
      </footer>
    </aside>
  );
}
